/**
 * Inline styling and chrome for the Driver.js onboarding tours.
 *
 * Everything here is applied as inline style properties from TypeScript rather than
 * through a stylesheet, for two reasons:
 *   1. driver.css ships unlayered, and unlayered CSS outranks any cascade layer, so
 *      Tailwind utilities and `@layer` rules both lose to it. Inline styles outrank
 *      everything, so the cascade stops being a concern.
 *   2. The rail, beacon, click tag, intro and finish screens are elements Driver does
 *      not render, so they have to be built here regardless.
 *
 * Colours are PrimeNG semantic tokens, so light and dark themes follow automatically.
 * The intro, finish and rail sit on a fixed dark stage by design.
 */
import type { PopoverDOM } from 'driver.js';

import type {
    GgxTourDefinition,
    GgxTourStep,
    TourChapter,
    TourIcon,
    TourImage,
    TourTab,
    TourVideo,
} from '@app/modules/learning-hub/constants/tour-registry';

const TOKEN = {
    font: 'var(--default-font)',
    mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    surface: 'var(--p-content-background)',
    border: 'var(--p-content-border-color)',
    hover: 'var(--p-content-hover-background)',
    text: 'var(--p-text-color)',
    body: 'color-mix(in srgb, var(--p-text-color) 88%, var(--p-content-background))',
    muted: 'var(--p-text-muted-color)',
    primary: 'var(--p-primary-color)',
    primaryHover: 'var(--p-primary-hover-color)',
    primaryLight: 'var(--p-primary-300)',
    primaryDeep: 'var(--p-primary-800)',
    primarySoft: 'color-mix(in srgb, var(--p-primary-color) 16%, transparent)',
    chipFill: 'color-mix(in srgb, var(--p-primary-color) 12%, var(--p-content-background))',
    chipFillHover: 'color-mix(in srgb, var(--p-primary-color) 20%, var(--p-content-background))',
    chipLine: 'color-mix(in srgb, var(--p-primary-color) 32%, var(--p-content-background))',
    chipLineHover: 'color-mix(in srgb, var(--p-primary-color) 48%, var(--p-content-background))',
    onPrimary: 'var(--p-primary-contrast-color)',
    accent: 'var(--p-orange-500)',
    accentText: 'var(--p-orange-700)',
    accentSoft: 'color-mix(in srgb, var(--p-orange-500) 18%, transparent)',
    accentLine: 'color-mix(in srgb, var(--p-orange-500) 28%, transparent)',
    ok: 'var(--p-green-500)',
    okSoft: 'color-mix(in srgb, var(--p-green-500) 15%, transparent)',
    stage: '#0b1220',
    stageMid: '#101c36',
    paper: '#f2f5fb',
    paperCard: '#ffffff',
    paperInk: '#101a2c',
    paperBody: '#3c4759',
    paperMute: '#59637a',
    paperLine: 'rgba(16,26,44,.10)',
    paperTile: '#e8eeff',
    brand: '#2a5ad6',
    brandDeep: '#1f47a8',
    okInk: '#137a44',
    okDisc: '#e4f4ea',
} as const;

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const Z = {
    marker: 999_999_999,
    rail: 1_000_000_001,
    takeover: 1_000_000_002,
    zoom: 1_000_000_003,
} as const;

const prefersReducedMotion = (): boolean =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const STROKE = 'fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"';

const ICON: Record<
    TourIcon | 'arrow' | 'back' | 'cursor' | 'check' | 'logo' | 'copy' | 'close',
    string
> = {
    copy: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><rect x="6" y="6" width="8" height="8" rx="1.6"/><path d="M3.5 10.5A1.5 1.5 0 0 1 2 9V3.5A1.5 1.5 0 0 1 3.5 2H9a1.5 1.5 0 0 1 1.5 1.5"/></svg>`,
    grid: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><rect x="2" y="2" width="5" height="5" rx="1.2"/><rect x="9" y="2" width="5" height="5" rx="1.2"/><rect x="2" y="9" width="5" height="5" rx="1.2"/><rect x="9" y="9" width="5" height="5" rx="1.2"/></svg>`,
    clock: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.5 1.5"/></svg>`,
    list: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><path d="M5 4h8M5 8h8M5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01"/></svg>`,
    search: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>`,
    cap: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><path d="M1.5 6.5L8 3l6.5 3.5L8 10z"/><path d="M4 8v3.5c1.2 1.2 6.8 1.2 8 0V8"/></svg>`,
    map: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><path d="M1.5 4l4-1.5 5 1.5 4-1.5v9.5l-4 1.5-5-1.5-4 1.5z"/><path d="M5.5 2.5v9.5M10.5 4v9.5"/></svg>`,
    pulse: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><path d="M1.5 8h3l2-4 3 8 2-4h3"/></svg>`,
    compass: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.6"><circle cx="8" cy="8" r="6.5"/><path d="M10.5 5.5L9 9l-3.5 1.5L7 7z"/></svg>`,
    arrow: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="2"><path d="M3 8h10M9 4l4 4-4 4"/></svg>`,
    back: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="2"><path d="M13 8H3M7 4L3 8l4 4"/></svg>`,
    cursor: `<svg viewBox="0 0 16 16" fill="currentColor"><path d="M3 2l10 5.2-4.3 1.2L7.4 13z"/></svg>`,
    check: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="2.2"><path d="M3 8.5l3 3 7-7"/></svg>`,
    close: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.8"><path d="M4 4l8 8M12 4l-8 8"/></svg>`,
    logo: `<svg viewBox="0 0 16 16" ${STROKE} stroke-width="1.8"><path d="M3 13V3l10 5z"/></svg>`,
};

type Styles = Partial<CSSStyleDeclaration>;

function el<K extends keyof HTMLElementTagNameMap>(
    doc: Document,
    tag: K,
    styles: Styles = {},
    text?: string,
): HTMLElementTagNameMap[K] {
    const node = doc.createElement(tag);
    Object.assign(node.style, { pointerEvents: 'auto', fontFamily: TOKEN.font }, styles);
    if (text !== undefined) node.textContent = text;
    return node;
}

function icon(
    doc: Document,
    name: keyof typeof ICON,
    px: number,
    styles: Styles = {},
): HTMLElement {
    const box = el(doc, 'span', {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${px}px`,
        height: `${px}px`,
        flex: 'none',
        ...styles,
    });
    box.innerHTML = ICON[name];
    const svg = box.firstElementChild as SVGElement | null;
    if (svg) {
        svg.setAttribute('width', String(px));
        svg.setAttribute('height', String(px));
        svg.style.display = 'block';
    }
    return box;
}

function animate(
    node: Element,
    keyframes: Keyframe[],
    options: KeyframeAnimationOptions,
): Animation | null {
    if (prefersReducedMotion()) return null;
    return node.animate(keyframes, { easing: EASE, fill: 'both', ...options });
}

function rise(node: Element, delayMs: number): void {
    animate(
        node,
        [
            { opacity: 0, transform: 'translateY(18px)' },
            { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 700, delay: delayMs },
    );
}

function button(
    doc: Document,
    label: string,
    variant: 'primary' | 'ghost' | 'light' | 'brand' | 'paper',
    options: { big?: boolean; leading?: keyof typeof ICON; trailing?: keyof typeof ICON } = {},
): HTMLButtonElement {
    const big = options.big ?? false;
    const base: Styles = {
        all: 'unset',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        fontFamily: TOKEN.font,
        fontSize: big ? '15.5px' : '14px',
        fontWeight: '600',
        lineHeight: '1',
        borderRadius: big ? '13px' : '11px',
        height: big ? '50px' : '42px',
        padding: big ? '0 24px' : '0 18px',
        border: '1px solid transparent',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: `transform .15s ${EASE}, background-color .15s, box-shadow .15s, color .15s`,
    };
    const variants: Record<typeof variant, { rest: Styles; hover: Styles }> = {
        primary: {
            rest: {
                backgroundColor: TOKEN.primary,
                color: TOKEN.onPrimary,
                boxShadow: `0 6px 16px -6px color-mix(in srgb, ${TOKEN.primary} 60%, transparent), inset 0 1px 0 rgba(255,255,255,.15)`,
                transform: 'translateY(0)',
            },
            hover: { backgroundColor: TOKEN.primaryHover, transform: 'translateY(-1px)' },
        },
        ghost: {
            rest: {
                backgroundColor: 'transparent',
                color: TOKEN.muted,
                fontWeight: '500',
                padding: '0 10px',
            },
            hover: { backgroundColor: TOKEN.hover, color: TOKEN.text },
        },
        light: {
            rest: {
                backgroundColor: 'rgba(255,255,255,.08)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,.14)',
            },
            hover: { backgroundColor: 'rgba(255,255,255,.14)' },
        },
        brand: {
            rest: {
                backgroundColor: TOKEN.brand,
                color: '#fff',
                boxShadow: '0 6px 16px -8px rgba(42,90,214,.55)',
                transform: 'translateY(0)',
            },
            hover: { backgroundColor: TOKEN.brandDeep, transform: 'translateY(-1px)' },
        },
        paper: {
            rest: {
                backgroundColor: TOKEN.paperCard,
                color: TOKEN.paperInk,
                borderColor: 'rgba(16,26,44,.18)',
                boxShadow: '0 1px 2px rgba(16,26,44,.06)',
            },
            hover: { backgroundColor: '#eaeff7' },
        },
    };
    const btn = el(doc, 'button', { ...base, ...variants[variant].rest });
    btn.type = 'button';
    if (options.leading) btn.appendChild(icon(doc, options.leading, 16));
    btn.appendChild(doc.createTextNode(label));
    if (options.trailing) btn.appendChild(icon(doc, options.trailing, 16));
    btn.addEventListener('mouseenter', () => Object.assign(btn.style, variants[variant].hover));
    btn.addEventListener('mouseleave', () => Object.assign(btn.style, variants[variant].rest));
    btn.addEventListener('focus', () => {
        let keyboard: boolean;
        try {
            keyboard = btn.matches(':focus-visible');
        } catch {
            // Keep the focus indicator if :focus-visible is unsupported.
            keyboard = true;
        }
        if (!keyboard) return;
        btn.style.outline = `2px solid ${TOKEN.primary}`;
        btn.style.outlineOffset = '2px';
    });
    btn.addEventListener('blur', () => {
        btn.style.outline = '';
    });
    return btn;
}

export interface StepCardContext {
    doc: Document;
    step: GgxTourStep;
    chapter?: TourChapter;
    chapterIndex?: number;
    isAction: boolean;
    stepNumber?: number;
    totalSteps?: number;
    onRelayout?: () => void;
}

/**
 * Tokens the code block colours: comments, strings (incl. triple-quoted), decorators and
 * Python keywords, in that order of precedence. Anything else stays in the base colour.
 * Prose pasted into a code block (a prompt template) is left almost untouched: only its
 * quoted phrases pick up the string colour.
 */
const CODE_TOKEN =
    /(#[^\n]*)|("""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|(@[\w.]+)|\b(import|from|as|def|return|for|in|if|elif|else|None|True|False|class|with|lambda|not|and|or|while|pass|yield|try|except|raise)\b/g;

const CODE_COLOUR = {
    comment: '#64748b',
    string: '#fcd34d',
    decorator: '#c4b5fd',
    keyword: '#93c5fd',
} as const;

const LOOKS_LIKE_CODE = /^\s*(#|@|import |from |def |class |[\w.]+\s*=[^=]|[\w.]+\()/m;

function highlightCode(doc: Document, pre: HTMLElement, code: string): void {
    pre.replaceChildren();
    if (!LOOKS_LIKE_CODE.test(code)) {
        pre.textContent = code;
        return;
    }
    let last = 0;
    const flush = (end: number) => {
        if (end > last) pre.appendChild(doc.createTextNode(code.slice(last, end)));
    };
    for (const m of code.matchAll(CODE_TOKEN)) {
        const start = m.index ?? 0;
        flush(start);
        const colour = m[1]
            ? CODE_COLOUR.comment
            : m[2]
              ? CODE_COLOUR.string
              : m[3]
                ? CODE_COLOUR.decorator
                : CODE_COLOUR.keyword;
        pre.appendChild(el(doc, 'span', { color: colour, fontFamily: 'inherit' }, m[0]));
        last = start + m[0].length;
    }
    flush(code.length);
}

/**
 * Turns interactive markers in step copy into live UI, styled inline (no stylesheet):
 * - `<code data-ggx-copy>value</code>` becomes a monospace value chip the user clicks to copy,
 *   so a value they must type (a name, an alias, a search term) stands out from the prose.
 * - `<pre data-ggx-code>...</pre>` becomes a dark code block with its own Copy button, so a
 *   snippet the user must paste into an editor reads as code, not skippable prose.
 *   `data-ggx-wrap` wraps long lines (for prose such as a prompt); `data-ggx-readonly` drops
 *   the Copy button (for an example to read, not paste).
 */
/** Fills `{{origin}}` in a snippet with this GGX's own address, so nobody has to look it up. */
function fillTokens(doc: Document, text: string): string {
    const origin = doc.defaultView?.location.origin ?? '';
    return origin ? text.split('{{origin}}').join(origin) : text;
}

/** Builds a Jupyter notebook from every copyable snippet in a tour, one section per step. */
function tourNotebook(doc: Document, definition: GgxTourDefinition): string {
    const cells: object[] = [];
    const lines = (text: string) => text.split(/(?<=\n)/);
    const addMarkdown = (text: string) =>
        cells.push({ cell_type: 'markdown', metadata: {}, source: lines(text) });
    const addCode = (text: string) =>
        cells.push({
            cell_type: 'code',
            metadata: {},
            execution_count: null,
            outputs: [],
            source: lines(text),
        });
    addMarkdown(
        `# ${definition.key.replace(/^\[[^\]]+\]\s*/, '')}\n\n` +
            'Every snippet from the tour, in order. Run the cells from top to bottom.',
    );
    for (const step of definition.steps) {
        const snippets: string[] = [];
        for (const html of [step.description, ...(step.tabs ?? []).map(tab => tab.html)]) {
            const box = doc.createElement('div');
            box.innerHTML = html;
            for (const pre of Array.from(
                box.querySelectorAll<HTMLElement>('[data-ggx-code]:not([data-ggx-readonly])'),
            )) {
                snippets.push(fillTokens(doc, (pre.textContent ?? '').replace(/^\n+|\n+$/g, '')));
            }
        }
        if (snippets.length === 0) continue;
        addMarkdown(`## ${step.title}`);
        snippets.forEach(addCode);
    }
    return JSON.stringify(
        {
            cells,
            metadata: {
                kernelspec: { name: 'python3', display_name: 'Python 3', language: 'python' },
                language_info: { name: 'python' },
            },
            nbformat: 4,
            nbformat_minor: 4,
        },
        null,
        1,
    );
}

/** Adds the outro's "Download as notebook" button, when the tour asks for one. */
function appendNotebookDownload(
    doc: Document,
    parent: HTMLElement,
    definition: GgxTourDefinition,
): void {
    const fileName = definition.outro?.notebook;
    if (!fileName) return;
    const wrap = el(doc, 'div', { marginTop: '22px', textAlign: 'center' });
    const btn = el(doc, 'button', {
        all: 'unset',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 18px',
        borderRadius: '10px',
        backgroundColor: TOKEN.primary,
        color: '#fff',
        fontFamily: TOKEN.font,
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    });
    btn.type = 'button';
    btn.append(icon(doc, 'copy', 14), doc.createTextNode('Download as notebook'));
    btn.addEventListener('click', () => {
        const blob = new Blob([tourNotebook(doc, definition)], {
            type: 'application/x-ipynb+json',
        });
        const url = URL.createObjectURL(blob);
        const link = doc.createElement('a');
        link.href = url;
        link.download = fileName;
        doc.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
    const hint = el(
        doc,
        'p',
        { fontSize: '13.5px', color: TOKEN.paperBody, margin: '8px 0 0' },
        'Then open JupyterHub from your profile menu and drag the file into it.',
    );
    wrap.append(btn, hint);
    parent.appendChild(wrap);
}

function decorateInteractiveContent(doc: Document, root: HTMLElement): void {
    const mono = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

    const flash = (node: HTMLElement) => {
        const prev = node.style.boxShadow;
        node.style.boxShadow = `0 0 0 2px ${TOKEN.ok}`;
        globalThis.setTimeout(() => {
            node.style.boxShadow = prev;
        }, 750);
    };
    const write = (text: string) => {
        try {
            void navigator.clipboard?.writeText(text);
        } catch {
            // Clipboard access is best-effort; the copy affordance should not break the tour.
        }
    };

    for (const chip of Array.from(root.querySelectorAll<HTMLElement>('[data-ggx-copy]'))) {
        const value = (chip.textContent ?? '').trim();
        const codeLike = !/\s/.test(value);
        const rest: Styles = {
            backgroundColor: TOKEN.chipFill,
            borderColor: TOKEN.chipLine,
        };
        Object.assign(chip.style, {
            display: 'inline-block',
            whiteSpace: 'normal',
            maxWidth: '100%',
            fontFamily: codeLike ? mono : TOKEN.font,
            fontSize: codeLike ? '12.5px' : '13px',
            fontWeight: codeLike ? '500' : '600',
            lineHeight: '1.45',
            color: TOKEN.primaryDeep,
            border: '1px solid transparent',
            borderRadius: '6px',
            padding: '1px 8px 1px 9px',
            cursor: 'pointer',
            pointerEvents: 'auto',
            transition: 'background-color .15s, border-color .15s',
            ...rest,
        } as Styles);
        chip.title = 'Click to copy';
        chip.appendChild(
            icon(doc, 'copy', 11, {
                marginLeft: '6px',
                color: TOKEN.primary,
                opacity: '.9',
                verticalAlign: '-1px',
            }),
        );
        chip.addEventListener('mouseenter', () =>
            Object.assign(chip.style, {
                backgroundColor: TOKEN.chipFillHover,
                borderColor: TOKEN.chipLineHover,
            }),
        );
        chip.addEventListener('mouseleave', () => Object.assign(chip.style, rest));
        chip.addEventListener('click', () => {
            write(value);
            flash(chip);
        });
    }

    for (const pre of Array.from(root.querySelectorAll<HTMLElement>('[data-ggx-code]'))) {
        const code = fillTokens(doc, (pre.textContent ?? '').replace(/^\n+/, ''));
        const labelText = pre.getAttribute('data-ggx-code')?.trim() || 'Paste into the editor';
        const wrap = pre.hasAttribute('data-ggx-wrap');
        const readonly = pre.hasAttribute('data-ggx-readonly');
        const box = el(doc, 'div', {
            margin: '6px 0 2px',
            border: '1px solid #1e293b',
            borderRadius: '10px',
            overflow: 'hidden',
        });
        const bar = el(doc, 'div', {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            backgroundColor: '#111827',
            padding: '7px 8px 7px 12px',
        });
        const label = el(
            doc,
            'span',
            {
                color: '#94a3b8',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '.05em',
                textTransform: 'uppercase',
            },
            labelText,
        );
        const btn = el(doc, 'button', {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'rgba(255,255,255,.10)',
            color: '#e6edff',
            border: '1px solid rgba(255,255,255,.18)',
            borderRadius: '7px',
            padding: '4px 10px',
            fontSize: '11.5px',
            fontWeight: '600',
            cursor: 'pointer',
        });
        btn.appendChild(icon(doc, 'copy', 13));
        const btnText = doc.createTextNode('Copy');
        btn.appendChild(btnText);
        bar.append(label);
        if (!readonly) bar.append(btn);
        const body = el(doc, 'pre', {
            margin: '0',
            backgroundColor: '#0b1220',
            color: '#e6edf3',
            padding: '13px 15px',
            fontFamily: mono,
            fontSize: '12.5px',
            lineHeight: '1.6',
            whiteSpace: wrap ? 'pre-wrap' : 'pre',
            overflowWrap: wrap ? 'anywhere' : 'normal',
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '240px',
        });
        highlightCode(doc, body, code);
        box.append(bar, body);
        pre.replaceWith(box);
        btn.addEventListener('click', () => {
            write(code);
            btnText.textContent = 'Copied';
            flash(btn);
            globalThis.setTimeout(() => {
                btnText.textContent = 'Copy';
            }, 1200);
        });
    }
}

function styleRichContent(doc: Document, root: HTMLElement, bodySize = '13.5px'): void {
    for (const p of Array.from(root.querySelectorAll<HTMLElement>('p'))) {
        Object.assign(p.style, {
            margin: '8px 0 0',
            fontFamily: TOKEN.font,
            fontSize: bodySize,
            lineHeight: '1.55',
            color: TOKEN.body,
        });
    }
    for (const strong of Array.from(root.querySelectorAll<HTMLElement>('b, strong'))) {
        strong.style.color = TOKEN.text;
        strong.style.fontWeight = '600';
    }
    for (const code of Array.from(
        root.querySelectorAll<HTMLElement>('code:not([data-ggx-copy]):not(pre code)'),
    )) {
        Object.assign(code.style, {
            fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: '12.5px',
            fontWeight: '500',
            lineHeight: '1.45',
            color: TOKEN.primaryDeep,
            backgroundColor: TOKEN.chipFill,
            border: `1px solid ${TOKEN.chipLine}`,
            borderRadius: '6px',
            padding: '0 6px',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        } as Styles);
    }
    for (const list of Array.from(root.querySelectorAll<HTMLElement>('ul[data-ggx-list]'))) {
        Object.assign(list.style, { margin: '6px 0 4px', padding: '0', listStyle: 'none' });
        for (const item of Array.from(list.querySelectorAll<HTMLElement>(':scope > li'))) {
            Object.assign(item.style, {
                display: 'flex',
                gap: '9px',
                alignItems: 'flex-start',
                margin: '5px 0',
                fontFamily: TOKEN.font,
                fontSize: bodySize,
                lineHeight: '1.55',
                color: TOKEN.body,
            });
            const mark = el(doc, 'i', {
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                backgroundColor: TOKEN.chipFill,
                border: `1px solid ${TOKEN.chipLine}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                marginTop: '2px',
                color: TOKEN.primary,
            });
            mark.appendChild(icon(doc, 'check', 10));
            const body = el(doc, 'span');
            body.append(...Array.from(item.childNodes));
            item.append(mark, body);
        }
    }
    for (const aside of Array.from(root.querySelectorAll<HTMLElement>('aside[data-ggx-use]'))) {
        const label = aside.getAttribute('data-ggx-use')?.trim() || 'Use case';
        Object.assign(aside.style, {
            display: 'block',
            margin: '10px 0 0',
            padding: '2px 0 2px 10px',
            borderLeft: `3px solid ${TOKEN.chipLine}`,
            fontFamily: TOKEN.font,
            fontSize: '13px',
            lineHeight: '1.5',
            color: TOKEN.body,
        });
        const tag = el(
            doc,
            'b',
            {
                display: 'block',
                marginBottom: '2px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: TOKEN.primaryDeep,
            },
            label,
        );
        aside.insertBefore(tag, aside.firstChild);
    }
}

function benefitRow(doc: Document, benefits: string[]): HTMLElement {
    const row = el(doc, 'div', {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        margin: '10px 0 2px',
    });
    row.dataset.ggxBenefits = '';
    for (const text of benefits) {
        const pill = el(doc, 'span', {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '999px',
            backgroundColor: TOKEN.chipFill,
            border: `1px solid ${TOKEN.chipLine}`,
            color: TOKEN.primaryDeep,
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '1.3',
        });
        pill.append(icon(doc, 'check', 12, { color: TOKEN.primary }), doc.createTextNode(text));
        row.appendChild(pill);
    }
    return row;
}

function tabBlock(doc: Document, tabs: TourTab[], onSwitch?: () => void): HTMLElement {
    const box = el(doc, 'div', { margin: '10px 0 0' });
    box.dataset.ggxTabs = '';
    const strip = el(doc, 'div', {
        display: 'inline-flex',
        gap: '2px',
        padding: '3px',
        borderRadius: '9px',
        backgroundColor: `color-mix(in srgb, ${TOKEN.text} 6%, ${TOKEN.surface})`,
    });
    strip.setAttribute('role', 'tablist');
    const buttons: HTMLButtonElement[] = [];
    const panes: HTMLElement[] = [];
    // A dot on tabs not opened yet, so nobody misses the second and third tab.
    const unread: HTMLElement[] = [];
    const select = (index: number) => {
        if (unread[index]) unread[index].style.display = 'none';
        buttons.forEach((b, i) => {
            const on = i === index;
            b.setAttribute('aria-selected', String(on));
            Object.assign(b.style, {
                backgroundColor: on ? TOKEN.surface : 'transparent',
                color: on ? TOKEN.text : TOKEN.muted,
                boxShadow: on ? '0 1px 2px rgba(15,23,42,.12)' : 'none',
            });
        });
        panes.forEach((p, i) => {
            p.style.display = i === index ? 'block' : 'none';
        });
    };
    tabs.forEach((tab, i) => {
        const btn = el(doc, 'button', {
            all: 'unset',
            pointerEvents: 'auto',
            boxSizing: 'border-box',
            fontFamily: TOKEN.font,
            fontSize: '12.5px',
            fontWeight: '600',
            lineHeight: '1',
            padding: '7px 12px',
            borderRadius: '7px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background-color .15s, color .15s',
        });
        btn.type = 'button';
        btn.setAttribute('role', 'tab');
        btn.textContent = tab.label;
        const dot = el(doc, 'span', {
            display: i === 0 ? 'none' : 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: TOKEN.primary,
            marginLeft: '6px',
            verticalAlign: '2px',
        });
        dot.setAttribute('aria-hidden', 'true');
        btn.appendChild(dot);
        unread.push(dot);
        btn.addEventListener('click', () => {
            select(i);
            onSwitch?.();
        });
        buttons.push(btn);
        strip.appendChild(btn);

        const pane = el(doc, 'div', { display: 'none' });
        pane.setAttribute('role', 'tabpanel');
        pane.innerHTML = tab.html;
        styleRichContent(doc, pane);
        decorateInteractiveContent(doc, pane);
        panes.push(pane);
    });
    box.appendChild(strip);
    for (const pane of panes) box.appendChild(pane);
    select(0);
    return box;
}

const EMBED_HOST =
    /(^|\/\/)(player\.)?(vimeo\.com|youtube\.com|youtube-nocookie\.com|youtu\.be)\//i;

/**
 * The video slot on a step card: a 16:9 frame under the description. With a `src` it plays
 * the video (native player for a media file, iframe for a Vimeo / YouTube URL); without one
 * it draws a labelled placeholder so the space is visibly reserved until the file arrives.
 */
function videoBlock(doc: Document, video: TourVideo): HTMLElement {
    const frame = el(doc, 'div', {
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        margin: '12px 0 0',
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: TOKEN.stage,
        border: `1px solid ${TOKEN.border}`,
    });
    frame.dataset.ggxVideo = '';
    const src = video.src.trim();
    if (src && EMBED_HOST.test(src)) {
        const iframe = doc.createElement('iframe');
        iframe.src = src;
        iframe.title = video.title;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.setAttribute('allowfullscreen', '');
        Object.assign(iframe.style, {
            position: 'absolute',
            inset: '0',
            width: '100%',
            height: '100%',
            border: '0',
            pointerEvents: 'auto',
        });
        frame.appendChild(iframe);
        return frame;
    }
    if (src) {
        const player = doc.createElement('video');
        player.src = src;
        player.controls = true;
        player.preload = 'metadata';
        player.playsInline = true;
        player.title = video.title;
        if (video.poster) player.poster = video.poster;
        Object.assign(player.style, {
            position: 'absolute',
            inset: '0',
            width: '100%',
            height: '100%',
            display: 'block',
            backgroundColor: TOKEN.stage,
            pointerEvents: 'auto',
        });
        frame.appendChild(player);
        return frame;
    }
    const holder = el(doc, 'div', {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        color: '#e6edf3',
        background: `radial-gradient(120% 90% at 50% 0%, ${TOKEN.stageMid}, ${TOKEN.stage})`,
    });
    const badge = el(doc, 'div', {
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,.10)',
        border: '1px solid rgba(255,255,255,.18)',
    });
    badge.appendChild(icon(doc, 'logo', 22, { color: '#fff', marginLeft: '3px' }));
    const title = el(doc, 'div', { fontSize: '14px', fontWeight: '600' }, video.title);
    const note = el(
        doc,
        'div',
        { fontSize: '12px', fontWeight: '500', color: '#94a3b8', letterSpacing: '.02em' },
        'Video coming soon',
    );
    holder.append(badge, title, note);
    frame.appendChild(holder);
    return frame;
}

function openImageZoom(doc: Document, src: string, alt: string): void {
    const view = doc.defaultView ?? window;
    const overlay = el(doc, 'div', {
        position: 'fixed',
        inset: '0',
        zIndex: String(Z.zoom),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        backgroundColor: 'rgba(10,15,25,.86)',
        cursor: 'zoom-out',
    });
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', alt);
    const img = doc.createElement('img');
    img.src = src;
    img.alt = alt;
    Object.assign(img.style, {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow: '0 24px 64px rgba(0,0,0,.45)',
        cursor: 'default',
    });
    const close = el(doc, 'button', {
        position: 'absolute',
        top: '16px',
        right: '16px',
        width: '36px',
        height: '36px',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,.3)',
        backgroundColor: 'rgba(255,255,255,.12)',
        color: '#fff',
        cursor: 'pointer',
    });
    close.type = 'button';
    close.setAttribute('aria-label', 'Close image');
    close.appendChild(icon(doc, 'close', 16));
    const onKey = (e: KeyboardEvent): void => {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (e.key === 'Escape') shut();
    };
    const shut = (): void => {
        view.removeEventListener('keydown', onKey, true);
        overlay.remove();
    };
    overlay.addEventListener('click', e => {
        e.stopPropagation();
        if (e.target !== img) shut();
    });
    view.addEventListener('keydown', onKey, true);
    overlay.append(img, close);
    doc.body.appendChild(overlay);
    close.focus();
}

function imageBlock(doc: Document, image: TourImage): HTMLElement {
    const block = el(doc, 'div', { margin: '12px 0 0' });
    block.dataset.ggxImage = '';
    const frame = el(doc, 'div', {
        position: 'relative',
        width: '100%',
        borderRadius: '10px',
        overflow: 'hidden',
        border: `1px solid ${TOKEN.border}`,
        backgroundColor: TOKEN.surface,
    });
    const src = image.src.trim();
    if (src) {
        const img = doc.createElement('img');
        img.src = src;
        img.alt = image.title;
        img.loading = 'lazy';
        Object.assign(img.style, {
            display: 'block',
            width: '100%',
            height: 'auto',
            cursor: 'zoom-in',
        });
        img.title = 'Click to enlarge';
        img.addEventListener('click', e => {
            e.stopPropagation();
            openImageZoom(doc, src, image.title);
        });
        frame.appendChild(img);
    } else {
        Object.assign(frame.style, { aspectRatio: '16 / 9', backgroundColor: TOKEN.stage });
        const holder = el(doc, 'div', {
            position: 'absolute',
            inset: '0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            color: '#e6edf3',
            background: `radial-gradient(120% 90% at 50% 0%, ${TOKEN.stageMid}, ${TOKEN.stage})`,
        });
        const badge = el(doc, 'div', {
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,.10)',
            border: '1px solid rgba(255,255,255,.18)',
        });
        badge.appendChild(icon(doc, 'grid', 22, { color: '#fff' }));
        const title = el(doc, 'div', { fontSize: '14px', fontWeight: '600' }, image.title);
        const note = el(
            doc,
            'div',
            { fontSize: '12px', fontWeight: '500', color: '#94a3b8', letterSpacing: '.02em' },
            'Image coming soon',
        );
        holder.append(badge, title, note);
        frame.appendChild(holder);
    }
    block.appendChild(frame);

    if (image.caption || image.sourceUrl) {
        const caption = el(doc, 'div', {
            marginTop: '8px',
            fontSize: '12.5px',
            lineHeight: '1.5',
            color: TOKEN.muted,
        });
        if (image.caption) caption.append(doc.createTextNode(image.caption));
        if (image.sourceUrl) {
            if (image.caption) caption.append(doc.createTextNode(' '));
            const link = doc.createElement('a');
            link.href = image.sourceUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = 'Source';
            Object.assign(link.style, {
                color: TOKEN.primary,
                fontWeight: '600',
                textDecoration: 'underline',
                pointerEvents: 'auto',
            });
            caption.appendChild(link);
        }
        block.appendChild(caption);
    }
    return block;
}

export function styleStepCard(dom: PopoverDOM, ctx: StepCardContext): void {
    const { doc, step, chapter, isAction } = ctx;

    Object.assign(dom.wrapper.style, {
        fontFamily: TOKEN.font,
        boxSizing: 'border-box',
        minWidth: '340px',
        maxWidth: '420px',
        padding: '18px 20px 16px 18px',
        color: TOKEN.text,
        backgroundColor: TOKEN.surface,
        border: 'none',
        borderLeft: `3px solid ${TOKEN.primary}`,
        borderRadius: '14px',
        boxShadow: '0 1px 3px rgba(15,23,42,.1), 0 26px 60px -20px rgba(15,23,42,.6)',
    });

    dom.wrapper.querySelector('[data-ggx-chapter]')?.remove();
    const row = el(doc, 'div', {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '8px',
    });
    row.dataset.ggxChapter = '';
    row.appendChild(
        el(
            doc,
            'span',
            {
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: TOKEN.primary,
            },
            chapter ? chapter.name : 'Tour',
        ),
    );
    if (ctx.stepNumber && ctx.totalSteps) {
        row.appendChild(
            el(
                doc,
                'span',
                {
                    fontFamily: TOKEN.mono,
                    fontSize: '11px',
                    fontWeight: '600',
                    color: TOKEN.muted,
                    whiteSpace: 'nowrap',
                },
                `Step ${ctx.stepNumber} of ${ctx.totalSteps}`,
            ),
        );
    }
    dom.wrapper.insertBefore(row, dom.title);

    Object.assign(dom.title.style, {
        display: 'block',
        margin: '0',
        fontFamily: TOKEN.font,
        fontSize: '17.5px',
        fontWeight: '600',
        letterSpacing: '-.005em',
        lineHeight: '1.32',
        color: TOKEN.text,
    });
    for (const em of Array.from(dom.title.querySelectorAll<HTMLElement>('em, b, strong'))) {
        em.style.color = TOKEN.text;
        em.style.fontStyle = 'normal';
        em.style.fontWeight = '600';
    }
    Object.assign(dom.description.style, {
        marginTop: '8px',
        fontFamily: TOKEN.font,
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '1.62',
        color: TOKEN.body,
    });
    for (const strong of Array.from(dom.description.querySelectorAll<HTMLElement>('b, strong'))) {
        strong.style.color = TOKEN.text;
        strong.style.fontWeight = '600';
    }
    const hasCodeBlock = dom.description.querySelector('[data-ggx-code]') !== null;
    styleRichContent(doc, dom.description, '14px');
    decorateInteractiveContent(doc, dom.description);
    dom.wrapper.style.maxWidth = step.tabs
        ? '660px'
        : hasCodeBlock || step.video || step.image
          ? '580px'
          : '420px';

    dom.wrapper.querySelector('[data-ggx-benefits]')?.remove();
    dom.wrapper.querySelector('[data-ggx-tabs]')?.remove();
    dom.wrapper.querySelector('[data-ggx-video]')?.remove();
    dom.wrapper.querySelector('[data-ggx-image]')?.remove();
    let tail: HTMLElement = dom.description;
    if (step.benefits && step.benefits.length > 0) {
        const row = benefitRow(doc, step.benefits);
        tail.insertAdjacentElement('afterend', row);
        tail = row;
    }
    if (step.tabs && step.tabs.length > 0) {
        const tabs = tabBlock(doc, step.tabs, ctx.onRelayout);
        tail.insertAdjacentElement('afterend', tabs);
        tail = tabs;
    }
    if (step.video) {
        const video = videoBlock(doc, step.video);
        tail.insertAdjacentElement('afterend', video);
        tail = video;
    }
    if (step.image) {
        const image = imageBlock(doc, step.image);
        tail.insertAdjacentElement('afterend', image);
        tail = image;
    }

    dom.wrapper.querySelector('[data-ggx-next]')?.remove();
    if (step.whatHappens) {
        const next = el(doc, 'div', {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '9px',
            backgroundColor: TOKEN.surface,
            border: `1px solid ${TOKEN.border}`,
            borderLeft: `3px solid ${TOKEN.primary}`,
            borderRadius: '8px',
            padding: '11px 13px',
            marginTop: '14px',
            fontSize: '13px',
            lineHeight: '1.5',
            color: TOKEN.body,
        });
        next.dataset.ggxNext = '';
        next.appendChild(
            icon(doc, 'arrow', 14, { color: TOKEN.primary, flexShrink: '0', marginTop: '2px' }),
        );
        const text = el(doc, 'span');
        text.append(
            el(doc, 'b', { color: TOKEN.text, fontWeight: '600' }, 'What happens: '),
            doc.createTextNode(step.whatHappens),
        );
        next.appendChild(text);
        tail.insertAdjacentElement('afterend', next);
    }

    Object.assign(dom.footer.style, {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        rowGap: '10px',
        marginTop: '20px',
    });
    dom.progress.style.display = 'none';
    dom.closeButton.style.display = 'none';
    Object.assign(dom.footerButtons.style, {
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: '1',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px',
        rowGap: '10px',
    });

    const prevHidden = dom.previousButton.style.display === 'none';
    dom.previousButton.classList.remove('driver-popover-btn-disabled');
    styleFooterButton(dom.previousButton, 'ghost');
    dom.previousButton.replaceChildren(icon(doc, 'back', 16), doc.createTextNode('Back'));
    dom.previousButton.style.display = prevHidden ? 'none' : 'inline-flex';

    dom.footerButtons.querySelector('[data-ggx-wait]')?.remove();
    if (isAction) {
        dom.nextButton.style.display = 'none';
        dom.footerButtons.appendChild(waitingCue(doc, step.actionLabel));
    } else if (step.waitCue) {
        dom.nextButton.style.display = 'none';
        dom.footerButtons.appendChild(waitingCue(doc, undefined, step.waitCue));
    } else {
        styleFooterButton(dom.nextButton, 'primary');
        dom.nextButton.replaceChildren(doc.createTextNode('Continue'));
        dom.nextButton.style.display = 'inline-flex';
    }

    styleArrow(dom);

    animate(
        dom.wrapper,
        [
            { opacity: 0, transform: 'translateY(6px)' },
            { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 160, fill: 'none' },
    );
}

function styleFooterButton(btn: HTMLButtonElement, variant: 'primary' | 'ghost'): void {
    const rest: Styles =
        variant === 'primary'
            ? {
                  backgroundColor: TOKEN.primary,
                  color: TOKEN.onPrimary,
                  fontWeight: '600',
                  padding: '0 18px',
                  boxShadow: `0 6px 16px -6px color-mix(in srgb, ${TOKEN.primary} 60%, transparent), inset 0 1px 0 rgba(255,255,255,.15)`,
                  marginLeft: 'auto',
              }
            : {
                  backgroundColor: 'transparent',
                  color: TOKEN.muted,
                  fontWeight: '600',
                  padding: '0 14px',
                  boxShadow: 'none',
                  marginLeft: '0',
              };
    Object.assign(btn.style, {
        all: 'unset',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        fontFamily: TOKEN.font,
        fontSize: '13px',
        lineHeight: '1',
        borderRadius: '9px',
        height: '36px',
        border: '1px solid transparent',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: `transform .15s ${EASE}, background-color .15s, color .15s`,
        ...rest,
    } as Styles);
    btn.onmouseenter = () =>
        Object.assign(
            btn.style,
            variant === 'primary'
                ? { backgroundColor: TOKEN.primaryHover, transform: 'translateY(-1px)' }
                : {
                      backgroundColor: TOKEN.hover,
                      color: TOKEN.text,
                  },
        );
    btn.onmouseleave = () => Object.assign(btn.style, { transform: 'translateY(0)', ...rest });
}

/**
 * "Your turn · click <control>" pill with a slow ripple; shown instead of Continue on steps
 * that wait for the user to act on the highlighted control. A custom cue such as
 * "Type the name to continue" reads as "Your turn · type the name".
 */
function waitingCue(doc: Document, actionLabel?: string, text?: string): HTMLElement {
    const cue = el(doc, 'span', {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        minHeight: '32px',
        padding: '5px 12px 5px 8px',
        borderRadius: '999px',
        backgroundColor: TOKEN.hover,
        border: `1px solid ${TOKEN.border}`,
        color: TOKEN.text,
        fontSize: '12.5px',
        fontWeight: '600',
        lineHeight: '1.35',
        flexShrink: '1',
        minWidth: '0',
        marginLeft: 'auto',
    });
    cue.dataset.ggxWait = '';
    const ring = el(doc, 'span', {
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'center',
        width: '18px',
        height: '18px',
        flexShrink: '0',
    });
    const dot = el(doc, 'i', {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: TOKEN.primary,
    });
    const ripple = el(doc, 'i', {
        position: 'absolute',
        inset: '0',
        borderRadius: '50%',
        border: `2px solid ${TOKEN.primary}`,
        opacity: '0',
    });
    animate(
        ripple,
        [
            { transform: 'scale(.45)', opacity: 0.9 },
            { transform: 'scale(1.35)', opacity: 0, offset: 0.8 },
            { transform: 'scale(1.35)', opacity: 0 },
        ],
        { duration: 1800, iterations: Number.POSITIVE_INFINITY, easing: 'ease-out' },
    );
    ring.append(dot, ripple);
    const phrase = text
        ? text.replace(/\s+to continue$/i, '').replace(/^./, c => c.toLowerCase())
        : actionLabel
          ? `click ${actionLabel}`
          : 'waiting for your click';
    const label = el(doc, 'span');
    label.append(
        doc.createTextNode('Your turn '),
        el(doc, 'span', { color: TOKEN.muted, fontWeight: '500' }, `· ${phrase}`),
    );
    cue.append(ring, label);
    return cue;
}

/**
 * Recolours Driver's CSS-border arrow to the card surface and enlarges it to 16px for the
 * first paint. `fitPopover` re-aims it at the target on every frame after that.
 */
function styleArrow(dom: PopoverDOM): void {
    requestAnimationFrame(() => {
        const arrow = dom.arrow;
        arrow.style.borderWidth = '8px';
        arrow.style.borderColor = 'transparent';
        const sides = ['top', 'right', 'bottom', 'left'] as const;
        const side = sides.find(s => arrow.classList.contains(`driver-popover-arrow-side-${s}`));
        if (!side) return;
        const prop = `border${side[0].toUpperCase()}${side.slice(1)}Color` as
            | 'borderTopColor'
            | 'borderRightColor'
            | 'borderBottomColor'
            | 'borderLeftColor';
        arrow.style[prop] = TOKEN.surface;
    });
}

export function featherOverlay(svg?: SVGSVGElement): void {
    if (!svg) return;
    Object.assign(svg.style, {
        filter: 'blur(3px)',
        transform: 'scale(1.01)',
        transformOrigin: 'center',
    });
}

export interface StageShape {
    padding: number;
    radius: number;
}

export function stageShapeFor(element: Element): StageShape {
    const r = element.getBoundingClientRect();
    const radiusOf = (e: Element | null) =>
        e ? Number.parseFloat(globalThis.getComputedStyle(e).borderRadius) || 0 : 0;
    const circular =
        Math.abs(r.width - r.height) <= 2 &&
        Math.max(radiusOf(element), radiusOf(element.firstElementChild)) >= r.width / 2 - 1;
    if (circular) return { padding: 4, radius: Math.ceil(r.width / 2 + 4) };
    if (Math.min(r.width, r.height) <= 56) return { padding: 7, radius: 12 };
    return { padding: 10, radius: 16 };
}

export class SectionProxy {
    readonly element: HTMLElement;
    private head: HTMLElement | null = null;
    private single: HTMLElement | null = null;
    private selector = '';
    private container: HTMLElement | null = null;
    private scroller: HTMLElement | null = null;
    private sticky: HTMLElement[] = [];
    private frame = 0;
    private last = '';

    constructor(
        private readonly doc: Document,
        private readonly onChange: () => void,
    ) {
        this.element = el(doc, 'div', {
            position: 'fixed',
            pointerEvents: 'none',
            visibility: 'hidden',
            zIndex: '-1',
        });
        doc.body.appendChild(this.element);
    }

    owns(element: Element | undefined): boolean {
        return element === this.element;
    }

    follow(head: HTMLElement, selector: string): HTMLElement {
        if (this.head === head && this.selector === selector) return this.element;
        this.single = null;
        this.head = head;
        this.selector = selector;
        this.container = this.findContainer();
        this.scroller = scrollParent(head);
        this.sticky = this.findSticky();
        this.revealHead();
        this.last = '';
        this.sync();
        this.loop();
        return this.element;
    }

    followElement(element: HTMLElement): HTMLElement {
        if (this.single === element) return this.element;
        this.head = null;
        this.single = element;
        this.selector = '';
        this.container = null;
        this.scroller = scrollParent(element);
        this.sticky = this.findSticky();
        this.revealHead();
        this.last = '';
        this.sync();
        this.loop();
        return this.element;
    }

    private anchor(): HTMLElement | null {
        return this.single ?? this.head;
    }

    stop(): void {
        cancelAnimationFrame(this.frame);
        this.head = null;
        this.single = null;
    }

    destroy(): void {
        this.stop();
        this.element.remove();
    }

    private revealHead(): void {
        if (!this.anchor() || !this.scroller) return;
        const rects = this.members().map(m => m.getBoundingClientRect());
        const top = Math.min(...rects.map(r => r.top));
        const bottom = Math.max(...rects.map(r => r.bottom));
        const view = this.scroller.getBoundingClientRect();
        const ceiling = this.ceiling(view.top) + 12;
        if (top >= ceiling && bottom <= view.bottom) return;
        this.scroller.scrollTop += top - ceiling;
    }

    private findSticky(): HTMLElement[] {
        const out: HTMLElement[] = [];
        for (let node = this.anchor(); node && node !== this.scroller; node = node.parentElement) {
            for (const child of Array.from(node.parentElement?.children ?? [])) {
                if (
                    child !== node &&
                    child instanceof HTMLElement &&
                    globalThis.getComputedStyle(child).position === 'sticky'
                ) {
                    out.push(child);
                }
            }
        }
        return out;
    }

    private ceiling(viewTop: number): number {
        let edge = viewTop;
        for (const bar of this.sticky) {
            const r = bar.getBoundingClientRect();
            if (r.height > 0 && r.top <= edge + 1) edge = Math.max(edge, r.bottom);
        }
        return edge;
    }

    private findContainer(): HTMLElement | null {
        for (let node = this.head?.parentElement ?? null; node; node = node.parentElement) {
            if (node.querySelectorAll(this.selector).length >= 2) return node;
        }
        return null;
    }

    private isHeadingBlock(el: Element): boolean {
        return el.matches(this.selector) || el.querySelector(this.selector) !== null;
    }

    private members(): HTMLElement[] {
        if (this.single) return [this.single];
        if (!this.head) return [];
        let start: HTMLElement = this.head;
        if (this.container) {
            while (start.parentElement && start.parentElement !== this.container) {
                start = start.parentElement;
            }
        }
        const out: HTMLElement[] = [start];
        for (
            let next = start.nextElementSibling;
            next && !this.isHeadingBlock(next);
            next = next.nextElementSibling
        ) {
            if (next instanceof HTMLElement) out.push(next);
        }
        return out;
    }

    private loop(): void {
        cancelAnimationFrame(this.frame);
        const tick = () => {
            if (!this.anchor()) return;
            this.sync();
            this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
    }

    private sync(): void {
        const rects = this.members()
            .map(m => m.getBoundingClientRect())
            .filter(r => r.width > 0 && r.height > 0);
        if (rects.length === 0) return;

        let left = Math.min(...rects.map(r => r.left));
        let top = Math.min(...rects.map(r => r.top));
        let right = Math.max(...rects.map(r => r.right));
        let bottom = Math.max(...rects.map(r => r.bottom));

        if (this.scroller) {
            const view = this.scroller.getBoundingClientRect();
            left = Math.max(left, view.left);
            right = Math.min(right, view.right);
            top = Math.max(top, this.ceiling(view.top));
            bottom = Math.min(bottom, view.bottom);
        }
        const vh = this.doc.documentElement.clientHeight;
        const vw = this.doc.documentElement.clientWidth;
        left = Math.max(left, 0);
        top = Math.max(top, 0);
        right = Math.min(right, vw);
        bottom = Math.min(bottom, vh);
        if (right <= left || bottom <= top) return;

        const key = `${left}|${top}|${right}|${bottom}`;
        if (key === this.last) return;
        this.last = key;
        Object.assign(this.element.style, {
            left: `${left}px`,
            top: `${top}px`,
            width: `${right - left}px`,
            height: `${bottom - top}px`,
        });
        this.onChange();
    }
}

function scrollParent(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const { overflowY } = globalThis.getComputedStyle(node);
        if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight
        ) {
            return node;
        }
    }
    return null;
}

export interface MarkerState {
    element: HTMLElement;
    stepNumber: number;
    isAction: boolean;
    padding?: number;
    radius?: number;
}

export class TargetMarker {
    private static readonly TAG_SHADOW = '0 10px 24px rgba(15,23,42,.35)';
    private readonly glow: HTMLElement;
    private readonly badge: HTMLElement;
    private readonly ring: HTMLElement;
    private readonly tag: HTMLElement;
    private readonly tagTail: HTMLElement;
    private readonly tagCursor: HTMLElement;
    private state: MarkerState | null = null;
    private frame = 0;
    private ringAnimation: Animation | null = null;
    private glowAnimation: Animation | null = null;
    private tagAnimation: Animation | null = null;
    private lastRect = '';

    constructor(
        private readonly doc: Document,
        private readonly onFrame?: () => void,
        private readonly onMove?: () => void,
        private readonly avoid?: () => DOMRect | null,
    ) {
        this.glow = el(doc, 'div', {
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: String(Z.marker),
            opacity: '0',
            boxSizing: 'border-box',
            transformOrigin: 'center',
            transition: 'box-shadow .2s',
        });
        this.badge = el(doc, 'div', {
            position: 'fixed',
            pointerEvents: 'none',
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            color: '#fff',
            fontSize: '13px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,.35)',
            zIndex: String(Z.marker),
            opacity: '0',
            transition: 'background-color .2s',
        });
        this.ring = el(doc, 'span', {
            position: 'absolute',
            inset: '-4px',
            borderRadius: '50%',
            border: '2px solid transparent',
            opacity: '0',
            pointerEvents: 'none',
        });
        this.badge.appendChild(this.ring);
        this.tagTail = el(doc, 'span', {
            position: 'absolute',
            top: '50%',
            width: '12px',
            height: '12px',
            marginTop: '-6px',
            backgroundColor: TOKEN.surface,
            transform: 'rotate(45deg)',
            borderRadius: '2px',
        });
        this.tag = el(doc, 'div', {
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: String(Z.marker),
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            padding: '6px 11px 6px 12px',
            borderRadius: '9px',
            backgroundColor: TOKEN.surface,
            color: TOKEN.text,
            fontSize: '12.5px',
            fontWeight: '700',
            whiteSpace: 'nowrap',
            boxShadow: TargetMarker.TAG_SHADOW,
            opacity: '0',
            transition: 'opacity .15s',
        });
        this.tagCursor = icon(doc, 'cursor', 13, { color: TOKEN.primary });
        this.tag.append(this.tagCursor, doc.createTextNode('Click here'), this.tagTail);
        doc.body.append(this.glow, this.badge, this.tag);
    }

    tagSize(): { width: number; height: number } {
        return { width: this.tag.offsetWidth || 110, height: this.tag.offsetHeight || 30 };
    }

    track(state: MarkerState): void {
        this.state = state;
        const colour = TOKEN.primary;

        this.ringAnimation?.cancel();
        this.glowAnimation?.cancel();
        this.tagAnimation?.cancel();

        if (state.isAction) {
            this.badge.style.opacity = '0';
            this.glow.style.transform = 'scale(1)';
            const glowColour = `color-mix(in srgb, ${colour} 40%, transparent)`;
            const shadow = (blur: number, spread: number) =>
                `0 0 0 2px ${TOKEN.surface}, 0 0 0 6px ${glowColour}, 0 0 ${blur}px ${spread}px ${glowColour}`;
            this.glow.style.boxShadow = shadow(22, 6);
            this.glowAnimation = animate(
                this.glow,
                [
                    { boxShadow: shadow(22, 6) },
                    { boxShadow: shadow(36, 12) },
                    { boxShadow: shadow(22, 6) },
                ],
                { duration: 2200, iterations: Number.POSITIVE_INFINITY, easing: EASE },
            );
            this.tag.style.opacity = '1';
            const halo = `color-mix(in srgb, ${colour} 18%, transparent)`;
            this.tagAnimation = animate(
                this.tag,
                [
                    { transform: 'scale(1)', boxShadow: TargetMarker.TAG_SHADOW },
                    {
                        transform: 'scale(1.08)',
                        boxShadow: `0 14px 30px rgba(15,23,42,.42), 0 0 0 4px ${halo}`,
                    },
                    { transform: 'scale(1)', boxShadow: TargetMarker.TAG_SHADOW },
                ],
                { duration: 1800, iterations: Number.POSITIVE_INFINITY, easing: 'ease-in-out' },
            );
        } else {
            this.tag.style.opacity = '0';
            this.glow.style.transform = 'scale(1)';
            const halo = `color-mix(in srgb, ${TOKEN.primary} 45%, transparent)`;
            this.glow.style.boxShadow = `0 0 0 2px rgba(255,255,255,.9), 0 0 0 5px ${halo}, 0 24px 60px -10px rgba(0,0,0,.6)`;
            this.badge.style.backgroundColor = colour;
            this.badge.replaceChildren(
                this.doc.createTextNode(String(state.stepNumber)),
                this.ring,
            );
            this.ring.style.borderColor = colour;
            this.ringAnimation = animate(
                this.ring,
                [
                    { transform: 'scale(.7)', opacity: 0.95 },
                    { transform: 'scale(2.2)', opacity: 0 },
                ],
                { duration: 1600, iterations: 3, fill: 'none' },
            );
        }

        this.lastRect = '';
        this.position();
        this.loop();
    }

    clear(): void {
        this.state = null;
        cancelAnimationFrame(this.frame);
        this.glow.style.opacity = '0';
        this.glow.style.transform = 'scale(1)';
        this.badge.style.opacity = '0';
        this.tag.style.opacity = '0';
        this.ringAnimation?.cancel();
        this.glowAnimation?.cancel();
        this.tagAnimation?.cancel();
    }

    destroy(): void {
        this.clear();
        this.glow.remove();
        this.badge.remove();
        this.tag.remove();
    }

    private loop(): void {
        cancelAnimationFrame(this.frame);
        const tick = () => {
            if (!this.state) return;
            this.position();
            this.onFrame?.();
            this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
    }

    private position(): void {
        if (!this.state) return;
        const shape = stageShapeFor(this.state.element);
        const pad = this.state.padding ?? shape.padding;
        const radius = this.state.radius ?? shape.radius;
        const r = this.state.element.getBoundingClientRect();
        const card = this.avoid?.() ?? null;
        const cardKey = card
            ? `${Math.round(card.left)}|${Math.round(card.top)}|${Math.round(card.width)}|${Math.round(card.height)}`
            : '';
        const key = `${r.left}|${r.top}|${r.width}|${r.height}|${pad}|${cardKey}`;
        if (key === this.lastRect) return;
        const ringMoved = !this.lastRect.startsWith(
            `${r.left}|${r.top}|${r.width}|${r.height}|${pad}|`,
        );
        this.lastRect = key;

        const x = r.left - pad;
        const y = r.top - pad;
        const w = r.width + pad * 2;
        const h = r.height + pad * 2;

        const box = {
            left: `${x}px`,
            top: `${y}px`,
            width: `${w}px`,
            height: `${h}px`,
            borderRadius: `${radius}px`,
        };
        Object.assign(this.glow.style, box, { opacity: '1' });
        if (ringMoved) this.onMove?.();

        const vw = this.doc.documentElement.clientWidth;
        const vh = this.doc.documentElement.clientHeight;
        const clear = (b: { left: number; top: number; width: number; height: number }) =>
            !card ||
            b.left + b.width + 6 <= card.left ||
            b.left >= card.right + 6 ||
            b.top + b.height + 6 <= card.top ||
            b.top >= card.bottom + 6;

        if (this.state.isAction) {
            const tagW = this.tag.offsetWidth || 110;
            const tagH = this.tag.offsetHeight || 30;
            const gap = 10;
            const midY = Math.min(Math.max(4, y + h / 2 - tagH / 2), vh - tagH - 4);
            const midX = Math.min(Math.max(4, x + w / 2 - tagW / 2), vw - tagW - 4);
            const spots: {
                side: 'left' | 'right' | 'top' | 'bottom';
                left: number;
                top: number;
            }[] = [
                { side: 'left', left: x - gap - tagW, top: midY },
                { side: 'right', left: x + w + gap, top: midY },
                { side: 'top', left: midX, top: y - gap - tagH },
                { side: 'bottom', left: midX, top: y + h + gap },
            ];
            const inView = (p: { left: number; top: number }) =>
                p.left >= 4 && p.left + tagW <= vw - 4 && p.top >= 4 && p.top + tagH <= vh - 4;
            const pick = spots.find(p => inView(p) && clear({ ...p, width: tagW, height: tagH }));
            this.tag.style.opacity = pick ? '1' : '0';
            if (!pick) return;
            const horizontal = pick.side === 'left' || pick.side === 'right';
            Object.assign(this.tag.style, {
                left: `${Math.min(Math.max(4, pick.left), vw - tagW - 4)}px`,
                top: `${Math.min(Math.max(4, pick.top), vh - tagH - 4)}px`,
                transformOrigin:
                    pick.side === 'left'
                        ? 'right center'
                        : pick.side === 'right'
                          ? 'left center'
                          : pick.side === 'top'
                            ? 'center bottom'
                            : 'center top',
            });
            Object.assign(this.tagTail.style, {
                left: pick.side === 'right' ? '-6px' : horizontal ? 'auto' : '50%',
                right: pick.side === 'left' ? '-6px' : 'auto',
                top: horizontal ? '50%' : pick.side === 'top' ? 'auto' : '-6px',
                bottom: pick.side === 'top' ? '-6px' : 'auto',
                marginTop: horizontal ? '-6px' : '0',
                marginLeft: horizontal ? '0' : '-6px',
            });
            this.tagCursor.style.transform = pick.side === 'left' ? 'scaleX(-1)' : 'none';
        }

        if (!this.state.isAction) {
            const half = 13;
            const corners = [
                { left: x - half, top: y - half },
                { left: x - half, top: y + h - half },
                { left: x + w - half, top: y - half },
                { left: x + w - half, top: y + h - half },
            ];
            const inView = (p: { left: number; top: number }) =>
                p.left >= 4 &&
                p.left + 2 * half <= vw - 4 &&
                p.top >= 4 &&
                p.top + 2 * half <= vh - 4;
            const pick = corners.find(
                p => inView(p) && clear({ ...p, width: 2 * half, height: 2 * half }),
            );
            if (!pick) {
                this.badge.style.opacity = '0';
                return;
            }
            Object.assign(this.badge.style, {
                left: `${Math.min(Math.max(4, pick.left), vw - 2 * half - 4)}px`,
                top: `${Math.min(Math.max(4, pick.top), vh - 2 * half - 4)}px`,
                opacity: '1',
            });
        }
    }
}

export interface RailOptions {
    title: string;
    chapters: TourChapter[];
    totalSteps: number;
    resumable: boolean;
    onPause: () => void;
}

export class TourRail {
    private readonly root: HTMLElement;
    private readonly chapterEls: { pill: HTMLElement; badge: HTMLElement }[] = [];
    private readonly fill: HTMLElement;
    private readonly count: HTMLElement;
    private onWindowClick: ((e: MouseEvent) => void) | null = null;

    constructor(
        private readonly doc: Document,
        private readonly options: RailOptions,
    ) {
        this.root = el(doc, 'div', {
            position: 'fixed',
            left: '0',
            right: '0',
            top: '0',
            height: '44px',
            boxSizing: 'border-box',
            backgroundColor: TOKEN.paperCard,
            color: TOKEN.paperInk,
            display: 'flex',
            alignItems: 'center',
            gap: '18px',
            padding: '0 20px',
            fontSize: '13px',
            borderBottom: `1px solid ${TOKEN.paperLine}`,
            boxShadow: '0 6px 18px rgba(16,26,44,.10)',
            zIndex: String(Z.rail),
            transform: 'translateY(-100%)',
            transition: `transform .45s ${EASE}, top .3s ${EASE}, bottom .3s ${EASE}`,
        });
        this.root.setAttribute('role', 'status');
        this.root.setAttribute('aria-live', 'polite');

        const brand = el(doc, 'span', {
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            fontWeight: '600',
            letterSpacing: '-.01em',
        });
        brand.appendChild(
            el(
                doc,
                'span',
                {
                    display: 'grid',
                    placeItems: 'center',
                    width: '22px',
                    height: '22px',
                    flex: 'none',
                    borderRadius: '7px',
                    backgroundColor: TOKEN.brand,
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '800',
                    lineHeight: '1',
                },
                'G',
            ),
        );
        const title = el(
            doc,
            'span',
            {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '300px',
            },
            options.title,
        );
        title.title = options.title;
        brand.appendChild(title);
        this.root.appendChild(brand);

        const chapters = el(doc, 'span', {
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
        });
        options.chapters.forEach((chapter, k) => {
            const pill = el(doc, 'span', {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '5px 10px',
                borderRadius: '999px',
                fontSize: '12.5px',
                color: TOKEN.paperMute,
                border: '1px solid transparent',
                whiteSpace: 'nowrap',
                transition: 'all .3s',
            });
            const badge = el(doc, 'b', {
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: '700',
                flex: 'none',
                backgroundColor: '#eef1f6',
                color: TOKEN.paperMute,
            });
            badge.textContent = String(k + 1);
            pill.append(badge, doc.createTextNode(chapter.name));
            chapters.appendChild(pill);
            this.chapterEls.push({ pill, badge });
        });
        this.root.appendChild(chapters);

        const track = el(doc, 'span', {
            display: 'block',
            flex: 'none',
            width: '180px',
            height: '5px',
            borderRadius: '3px',
            backgroundColor: 'rgba(16,26,44,.12)',
            overflow: 'hidden',
        });
        this.fill = el(doc, 'i', {
            display: 'block',
            height: '100%',
            width: '0%',
            borderRadius: '3px',
            backgroundColor: TOKEN.brand,
            transition: `width .5s ${EASE}`,
        });
        track.appendChild(this.fill);
        this.root.appendChild(track);

        this.count = el(doc, 'span', {
            fontFamily: TOKEN.mono,
            fontSize: '11.5px',
            color: TOKEN.paperMute,
            whiteSpace: 'nowrap',
            minWidth: '78px',
            textAlign: 'right',
        });
        this.root.appendChild(this.count);

        const exit = el(doc, 'button', {
            all: 'unset',
            pointerEvents: 'auto',
            boxSizing: 'border-box',
            fontFamily: TOKEN.font,
            fontSize: '13px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            flex: 'none',
            whiteSpace: 'nowrap',
            color: TOKEN.paperInk,
            backgroundColor: TOKEN.paperCard,
            border: '1px solid rgba(16,26,44,.16)',
            borderRadius: '9px',
            height: '32px',
            padding: '0 13px',
            cursor: 'pointer',
            transition: 'background-color .15s, border-color .15s',
        });
        exit.type = 'button';
        exit.append(
            el(
                doc,
                'span',
                { fontSize: '15px', lineHeight: '1', color: TOKEN.paperMute },
                '\u00d7',
            ),
            doc.createTextNode('Exit tour'),
        );
        exit.title = options.resumable
            ? 'Leave the tour. It picks up from this step next time you sign in.'
            : 'Leave the tour. You can start it again from the Learning Hub.';
        exit.addEventListener('mouseenter', () => {
            exit.style.backgroundColor = '#f4f6fa';
            exit.style.borderColor = 'rgba(16,26,44,.26)';
        });
        exit.addEventListener('mouseleave', () => {
            exit.style.backgroundColor = TOKEN.paperCard;
            exit.style.borderColor = 'rgba(16,26,44,.16)';
        });
        this.onWindowClick = (e: MouseEvent) => {
            if (!(e.target instanceof Node) || !exit.contains(e.target)) return;
            e.stopPropagation();
            e.preventDefault();
            options.onPause();
        };
        window.addEventListener('click', this.onWindowClick, true);
        this.root.appendChild(exit);

        doc.body.appendChild(this.root);
    }

    private side: 'top' | 'bottom' = 'top';

    show(): void {
        requestAnimationFrame(() => {
            this.root.style.transform = 'translateY(0)';
        });
    }

    hide(): void {
        this.root.style.transform = this.side === 'top' ? 'translateY(-100%)' : 'translateY(100%)';
    }

    static readonly HEIGHT = 44;

    get dockedAt(): 'top' | 'bottom' {
        return this.side;
    }

    dock(side: 'top' | 'bottom'): void {
        if (this.side === side) return;
        this.side = side;
        Object.assign(this.root.style, {
            top: side === 'top' ? '0' : 'auto',
            bottom: side === 'bottom' ? '0' : 'auto',
            borderBottom: side === 'top' ? `1px solid ${TOKEN.paperLine}` : 'none',
            borderTop: side === 'bottom' ? `1px solid ${TOKEN.paperLine}` : 'none',
            boxShadow:
                side === 'top' ? '0 6px 18px rgba(16,26,44,.10)' : '0 -6px 18px rgba(16,26,44,.10)',
        });
    }

    update(stepNumber: number): void {
        const chapterIndex = this.options.chapters.findIndex(c => c.steps.includes(stepNumber));
        this.chapterEls.forEach(({ pill, badge }, k) => {
            const done = k < chapterIndex;
            const on = k === chapterIndex;
            Object.assign(pill.style, {
                color: on || done ? TOKEN.paperInk : TOKEN.paperMute,
                backgroundColor: on ? '#eef3ff' : 'transparent',
                borderColor: on ? 'rgba(42,90,214,.22)' : 'transparent',
            });
            badge.style.backgroundColor = on ? TOKEN.brand : done ? TOKEN.okDisc : '#eef1f6';
            badge.style.color = on ? '#fff' : done ? TOKEN.okInk : TOKEN.paperMute;
            if (done) {
                badge.replaceChildren(icon(this.doc, 'check', 10));
            } else {
                badge.textContent = String(k + 1);
            }
        });
        this.count.textContent = `Step ${stepNumber} of ${this.options.totalSteps}`;
        this.fill.style.width = `${(stepNumber / this.options.totalSteps) * 100}%`;
    }

    destroy(): void {
        if (this.onWindowClick) window.removeEventListener('click', this.onWindowClick, true);
        this.onWindowClick = null;
        this.root.remove();
    }
}

export type CardSide = 'top' | 'right' | 'bottom' | 'left';

export interface FitOptions {
    target: HTMLElement;
    padding: number;
    preferred?: CardSide | 'over';
    align?: 'start' | 'center' | 'end';
    topInset: number;
    bottomInset: number;
    tag?: { width: number; height: number };
}

const FIT_MARGIN = 16;
const FIT_GAP = 22;
const FIT_GLOW = 8;

export function fitPopover(
    doc: Document,
    wrapper: HTMLElement,
    arrow: HTMLElement | null,
    options: FitOptions,
): CardSide | null {
    const side = fitOnce(doc, wrapper, arrow, options);
    if (side) return side;
    const original = wrapper.style.maxWidth;
    if (Number.parseFloat(original) <= 420) {
        wrapper.style.maxWidth = '340px';
        const narrow = fitOnce(doc, wrapper, arrow, options);
        if (narrow) return narrow;
        wrapper.style.maxWidth = original;
    }
    const WIDE = '560px';
    if (wrapper.style.maxWidth !== WIDE && Number.parseFloat(wrapper.style.maxWidth) < 560) {
        wrapper.style.maxWidth = WIDE;
        const wide = fitOnce(doc, wrapper, arrow, options);
        if (wide) return wide;
    }
    const vh = doc.documentElement.clientHeight;
    const t = options.target.getBoundingClientRect();
    const pad = options.padding + FIT_GLOW;
    const band = Math.max(
        vh - options.bottomInset - FIT_MARGIN - (t.bottom + pad + FIT_GAP),
        t.top - pad - FIT_GAP - (options.topInset + FIT_MARGIN),
    );
    if (band >= 260 && fitMediaTo(wrapper, band)) {
        const shrunk = fitOnce(doc, wrapper, arrow, options);
        if (shrunk) return shrunk;
    }
    return null;
}

export function fitMediaTo(wrapper: HTMLElement, room: number): boolean {
    const frames = Array.from(
        wrapper.querySelectorAll<HTMLElement>('[data-ggx-video], [data-ggx-image] > div'),
    );
    let excess = wrapper.scrollHeight - room;
    if (excess <= 0 || frames.length === 0) return false;
    let trimmed = false;
    for (const frame of frames) {
        const current = frame.getBoundingClientRect().height;
        const next = Math.max(120, current - excess);
        if (next >= current) continue;
        frame.style.maxHeight = `${Math.round(next)}px`;
        const img = frame.querySelector<HTMLElement>('img');
        if (img) Object.assign(img.style, { height: '100%', objectFit: 'contain' });
        excess -= current - next;
        trimmed = true;
    }
    return trimmed;
}

function fitOnce(
    doc: Document,
    wrapper: HTMLElement,
    arrow: HTMLElement | null,
    options: FitOptions,
): CardSide | null {
    const vw = doc.documentElement.clientWidth;
    const vh = doc.documentElement.clientHeight;
    const t = options.target.getBoundingClientRect();
    if (t.width === 0 && t.height === 0) return null;
    const pad = options.padding + FIT_GLOW;
    const ring = {
        left: t.left - pad,
        top: t.top - pad,
        right: t.right + pad,
        bottom: t.bottom + pad,
    };
    const w = wrapper.offsetWidth;
    const h = wrapper.offsetHeight;
    if (!w || !h) return null;

    const minTop = options.topInset + FIT_MARGIN;
    const maxBottom = vh - options.bottomInset - FIT_MARGIN;
    const clampX = (x: number) => Math.min(Math.max(FIT_MARGIN, x), vw - w - FIT_MARGIN);
    const clampY = (y: number) => Math.min(Math.max(minTop, y), maxBottom - h);
    const alongX = () => {
        const cx = (ring.left + ring.right) / 2;
        if (options.align === 'start') return clampX(t.left);
        if (options.align === 'end') return clampX(t.right - w);
        return clampX(cx - w / 2);
    };
    const alongY = () => clampY((ring.top + ring.bottom) / 2 - h / 2);

    const place: Record<CardSide, { left: number; top: number; fits: boolean }> = {
        bottom: {
            left: alongX(),
            top: ring.bottom + FIT_GAP,
            fits: ring.bottom + FIT_GAP + h <= maxBottom,
        },
        top: {
            left: alongX(),
            top: ring.top - FIT_GAP - h,
            fits: ring.top - FIT_GAP - h >= minTop,
        },
        right: {
            left: ring.right + FIT_GAP,
            top: alongY(),
            fits: ring.right + FIT_GAP + w <= vw - FIT_MARGIN && h <= maxBottom - minTop,
        },
        left: {
            left: ring.left - FIT_GAP - w,
            top: alongY(),
            fits: ring.left - FIT_GAP - w >= FIT_MARGIN && h <= maxBottom - minTop,
        },
    };
    const order: CardSide[] = ['bottom', 'right', 'top', 'left'];
    if (options.preferred && options.preferred !== 'over') {
        order.splice(order.indexOf(options.preferred), 1);
        order.unshift(options.preferred);
    }
    const tagHasSpot = (k: CardSide): boolean => {
        const tag = options.tag;
        if (!tag) return true;
        const box = { left: t.left - options.padding, top: t.top - options.padding };
        const bw = t.width + options.padding * 2;
        const bh = t.height + options.padding * 2;
        const gap = 10;
        const midY = box.top + bh / 2 - tag.height / 2;
        const midX = box.left + bw / 2 - tag.width / 2;
        const spots = [
            { left: box.left - gap - tag.width, top: midY },
            { left: box.left + bw + gap, top: midY },
            { left: midX, top: box.top - gap - tag.height },
            { left: midX, top: box.top + bh + gap },
        ];
        const card = {
            left: place[k].left,
            top: place[k].top,
            right: place[k].left + w,
            bottom: place[k].top + h,
        };
        return spots.some(
            p =>
                p.left >= 4 &&
                p.left + tag.width <= vw - 4 &&
                p.top >= 4 &&
                p.top + tag.height <= vh - 4 &&
                (p.left + tag.width + 6 <= card.left ||
                    p.left >= card.right + 6 ||
                    p.top + tag.height + 6 <= card.top ||
                    p.top >= card.bottom + 6),
        );
    };
    const side = order.find(k => place[k].fits && tagHasSpot(k)) ?? order.find(k => place[k].fits);
    if (!side) return null;

    const spot = place[side];
    Object.assign(wrapper.style, {
        left: `${Math.round(spot.left)}px`,
        top: `${Math.round(spot.top)}px`,
        right: 'auto',
        bottom: 'auto',
    });

    if (arrow) {
        const half = 8;
        arrow.style.display = '';
        arrow.style.borderWidth = `${half}px`;
        arrow.style.borderColor = 'transparent';
        for (const s of ['top', 'right', 'bottom', 'left'] as const) {
            arrow.classList.toggle(`driver-popover-arrow-side-${s}`, s === side);
        }
        for (const a of ['start', 'center', 'end'] as const) {
            arrow.classList.remove(`driver-popover-arrow-align-${a}`);
        }
        arrow.classList.remove('driver-popover-arrow-none');
        const prop = `border${side[0].toUpperCase()}${side.slice(1)}Color` as
            | 'borderTopColor'
            | 'borderRightColor'
            | 'borderBottomColor'
            | 'borderLeftColor';
        arrow.style[prop] = TOKEN.surface;
        if (side === 'top' || side === 'bottom') {
            const cx = (t.left + t.right) / 2 - spot.left - half;
            arrow.style.left = `${Math.round(Math.min(Math.max(14, cx), w - 2 * half - 14))}px`;
            arrow.style.top = '';
        } else {
            const cy = (t.top + t.bottom) / 2 - spot.top - half;
            arrow.style.top = `${Math.round(Math.min(Math.max(14, cy), h - 2 * half - 14))}px`;
            arrow.style.left = '';
        }
    }
    return side;
}

function stage(doc: Document): HTMLElement {
    const root = el(doc, 'div', {
        position: 'fixed',
        inset: '0',
        zIndex: String(Z.takeover),
        color: TOKEN.paperInk,
        backgroundColor: TOKEN.paper,
        overflow: 'auto',
    });
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    const dots = el(doc, 'div', {
        position: 'absolute',
        inset: '0',
        pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(16,26,44,.05) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
    });
    root.appendChild(dots);
    animate(root, [{ opacity: 0 }, { opacity: 1 }], { duration: 350, fill: 'none' });
    return root;
}

function headline(doc: Document, text: string, px: number): HTMLElement {
    const h = el(doc, 'h2', {
        fontSize: `${px}px`,
        lineHeight: '1.08',
        letterSpacing: '-.03em',
        fontWeight: '600',
        margin: '18px 0 0',
        color: TOKEN.paperInk,
    });
    text.split(' ').forEach((word, k) => {
        const span = el(doc, 'span', { display: 'inline-block' }, word);
        h.appendChild(span);
        if (k < text.split(' ').length - 1) h.appendChild(doc.createTextNode(' '));
        rise(span, k * 80);
    });
    return h;
}

function kicker(doc: Document, text: string): HTMLElement {
    return el(
        doc,
        'div',
        {
            fontFamily: TOKEN.mono,
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '.04em',
            color: TOKEN.brand,
        },
        text,
    );
}

function column(doc: Document): HTMLElement {
    return el(doc, 'div', {
        position: 'relative',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '48px 32px',
    });
}

function boldMarked(doc: Document, parent: HTMLElement, text: string): void {
    text.split('**').forEach((part, k) => {
        if (!part) return;
        if (k % 2 === 1) {
            parent.appendChild(el(doc, 'b', { color: TOKEN.paperInk, fontWeight: '700' }, part));
        } else {
            parent.appendChild(doc.createTextNode(part));
        }
    });
}

function paperCard(doc: Document, title: string): { card: HTMLElement; body: HTMLElement } {
    const card = el(doc, 'div', {
        backgroundColor: TOKEN.paperCard,
        border: `1px solid ${TOKEN.paperLine}`,
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(16,26,44,.10), 0 2px 5px rgba(16,26,44,.05)',
        textAlign: 'left',
    });
    card.appendChild(
        el(
            doc,
            'div',
            {
                backgroundColor: TOKEN.brand,
                color: '#fff',
                fontSize: '12.5px',
                fontWeight: '700',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                padding: '13px 22px',
            },
            title,
        ),
    );
    const body = el(doc, 'div', { padding: '4px 22px 16px' });
    card.appendChild(body);
    return { card, body };
}

function paperTile(doc: Document, name: keyof typeof ICON, tint = TOKEN.paperTile): HTMLElement {
    return icon(doc, name, 16, {
        width: '32px',
        height: '32px',
        borderRadius: '9px',
        backgroundColor: tint,
        color: TOKEN.brand,
        marginTop: '1px',
    });
}

function paperRow(
    doc: Document,
    parts: { tile: HTMLElement; title: string; detail?: string; meta?: string; last: boolean },
): HTMLElement {
    const row = el(doc, 'div', {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        padding: '13px 0',
        borderBottom: parts.last ? '0' : `1px solid ${TOKEN.paperLine}`,
    });
    row.appendChild(parts.tile);
    const text = el(doc, 'div', { minWidth: '0' });
    text.appendChild(
        el(
            doc,
            'div',
            { fontSize: '15px', fontWeight: '600', color: TOKEN.paperInk, lineHeight: '1.35' },
            parts.title,
        ),
    );
    if (parts.detail) {
        text.appendChild(
            el(
                doc,
                'div',
                {
                    fontSize: '13px',
                    color: TOKEN.paperMute,
                    lineHeight: '1.45',
                    marginTop: '3px',
                },
                parts.detail,
            ),
        );
    }
    row.appendChild(text);
    if (parts.meta) {
        row.appendChild(
            el(
                doc,
                'span',
                {
                    marginLeft: 'auto',
                    paddingTop: '4px',
                    fontFamily: TOKEN.mono,
                    fontSize: '11.5px',
                    color: TOKEN.paperMute,
                    whiteSpace: 'nowrap',
                },
                parts.meta,
            ),
        );
    }
    return row;
}

function brandRow(doc: Document, text: string): HTMLElement {
    const row = el(doc, 'div', {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '18px',
    });
    row.appendChild(
        el(
            doc,
            'span',
            {
                display: 'grid',
                placeItems: 'center',
                width: '30px',
                height: '30px',
                flex: 'none',
                borderRadius: '8px',
                backgroundColor: TOKEN.brand,
                color: '#fff',
                fontSize: '15px',
                fontWeight: '800',
                lineHeight: '1',
            },
            'G',
        ),
    );
    row.appendChild(
        el(
            doc,
            'span',
            {
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: TOKEN.brand,
            },
            text,
        ),
    );
    return row;
}

export interface IntroOptions {
    definition: GgxTourDefinition;
    eyebrow: string;
    userName: string;
    totalSteps: number;
    onBegin: () => void;
    onSkip?: () => void;
}

export function showIntro(doc: Document, options: IntroOptions): () => void {
    const { definition, eyebrow, userName, totalSteps, onBegin, onSkip } = options;
    const root = stage(doc);
    const col = column(doc);
    const chapters = definition.chapters ?? [];
    const wide = chapters.length > 0;

    const twoUp = wide && (doc.defaultView?.innerWidth ?? 1200) >= 940;
    const inner = el(doc, 'div', {
        display: 'grid',
        gridTemplateColumns: twoUp ? '1.05fr .95fr' : '1fr',
        gap: twoUp ? '60px' : '32px',
        alignItems: 'start',
        width: '100%',
        maxWidth: wide ? '1010px' : '560px',
        textAlign: 'left',
    });

    const left = el(doc, 'div', {});
    const brand = brandRow(doc, `GenGuardX · ${eyebrow}`);
    rise(brand, 0);
    left.appendChild(brand);

    const intro = definition.intro ?? { headline: definition.key, subtitle: '' };
    const h = headline(doc, intro.headline.replace('{name}', userName), 44);
    h.style.margin = '0 0 14px';
    h.style.fontWeight = '700';
    h.style.letterSpacing = '-.025em';
    h.style.lineHeight = '1.1';
    left.appendChild(h);

    const sub = el(
        doc,
        'p',
        {
            fontSize: '17px',
            color: TOKEN.paperBody,
            margin: '0 0 26px',
            lineHeight: '1.5',
        },
        intro.subtitle,
    );
    rise(sub, 150);
    left.appendChild(sub);

    const cta = el(doc, 'div', {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    });
    const begin = button(doc, 'Begin tour', 'brand', { big: true });
    begin.addEventListener('click', () => onBegin());
    cta.appendChild(begin);
    if (onSkip) {
        const skip = button(doc, 'Explore on my own', 'paper', { big: true });
        skip.addEventListener('click', () => onSkip());
        cta.appendChild(skip);
    }
    rise(cta, 300);
    left.appendChild(cta);

    const minutes = definition.estimatedMinutes
        ? ` · about ${definition.estimatedMinutes} min`
        : '';
    const meta = el(
        doc,
        'div',
        { fontSize: '13px', color: TOKEN.paperMute, margin: '14px 0 0' },
        `${totalSteps} steps${minutes}`,
    );
    rise(meta, 360);
    left.appendChild(meta);

    const clicks = definition.steps.filter(step => step.advanceOnTargetClick).length;
    const clickText =
        clicks === 0
            ? 'Nothing to click: read each card and press Continue.'
            : `You will click ${clicks} ${clicks === 1 ? 'time' : 'times'}; the coach explains each one.`;
    const hint = el(
        doc,
        'p',
        { fontSize: '12.5px', color: TOKEN.paperMute, margin: '8px 0 0' },
        `${clickText} Pause any time from the top bar.`,
    );
    rise(hint, 420);
    left.appendChild(hint);
    inner.appendChild(left);

    if (wide) {
        const { card, body } = paperCard(doc, "What you'll learn");
        chapters.forEach((chapter, k) => {
            const n = chapter.steps.length;
            body.appendChild(
                paperRow(doc, {
                    tile: paperTile(doc, chapter.icon),
                    title: chapter.name,
                    detail: chapter.blurb,
                    meta: `${n} ${n === 1 ? 'step' : 'steps'}`,
                    last: k === chapters.length - 1,
                }),
            );
        });
        rise(card, 250);
        inner.appendChild(card);
    }

    col.appendChild(inner);
    root.appendChild(col);
    doc.body.appendChild(root);
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onBegin();
        }
    };
    doc.addEventListener('keydown', onKey);
    begin.focus({ preventScroll: true });

    return () => {
        doc.removeEventListener('keydown', onKey);
        root.remove();
    };
}

export interface OutroNext {
    title: string;
    minutes?: number;
    steps?: number;
}

export interface OutroOptions {
    definition: GgxTourDefinition;
    totalSteps: number;
    elapsedMs?: number;
    next?: OutroNext;
    primaryLabel: string;
    onPrimary: () => void;
    secondaryLabel: string;
    onSecondary: () => void;
}

function elapsedLabel(ms: number): string {
    const total = Math.max(1, Math.round(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function showOutro(doc: Document, options: OutroOptions): () => void {
    const { definition, totalSteps, elapsedMs, next, primaryLabel, onPrimary } = options;
    const { secondaryLabel, onSecondary } = options;
    const root = stage(doc);
    const col = column(doc);
    const inner = el(doc, 'div', { width: '100%', maxWidth: '620px' });

    const badge = icon(doc, 'check', 34, {
        width: '84px',
        height: '84px',
        borderRadius: '50%',
        backgroundColor: TOKEN.okDisc,
        color: TOKEN.okInk,
        margin: '0 auto',
    });
    rise(badge, 0);
    inner.appendChild(badge);

    const kick = kicker(doc, `Tour complete · ${totalSteps} of ${totalSteps} steps`);
    kick.style.marginTop = '20px';
    rise(kick, 60);
    inner.appendChild(kick);

    const outro = definition.outro ?? { title: 'All done.', subtitle: '' };
    const h = headline(doc, outro.title, 40);
    h.style.margin = '10px 0 0';
    h.style.fontWeight = '700';
    h.style.letterSpacing = '-.025em';
    inner.appendChild(h);

    const learned = outro.learned ?? [];
    if (learned.length > 0) {
        const lead = el(
            doc,
            'p',
            { fontSize: '16px', color: TOKEN.paperBody, margin: '10px 0 0' },
            `${learned.length === 3 ? 'Three' : String(learned.length)} things you now know:`,
        );
        rise(lead, 150);
        inner.appendChild(lead);

        const { card, body } = paperCard(doc, 'What you now know');
        card.style.margin = '20px auto 0';
        card.style.maxWidth = '560px';
        learned.forEach((line, k) => {
            const row = el(doc, 'div', {
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '13px 0',
                borderBottom: k === learned.length - 1 ? '0' : `1px solid ${TOKEN.paperLine}`,
            });
            row.appendChild(
                icon(doc, 'check', 14, {
                    width: '28px',
                    height: '28px',
                    marginTop: '1px',
                    borderRadius: '50%',
                    backgroundColor: TOKEN.okDisc,
                    color: TOKEN.okInk,
                }),
            );
            const text = el(doc, 'span', {
                fontSize: '14.5px',
                lineHeight: '1.5',
                color: TOKEN.paperBody,
            });
            boldMarked(doc, text, line);
            row.appendChild(text);
            body.appendChild(row);
        });
        rise(card, 220);
        inner.appendChild(card);
        appendNotebookDownload(doc, inner, definition);
    } else {
        const sub = el(
            doc,
            'p',
            {
                fontSize: '16px',
                color: TOKEN.paperBody,
                maxWidth: '520px',
                margin: '10px auto 0',
                lineHeight: '1.55',
            },
            outro.subtitle,
        );
        rise(sub, 150);
        inner.appendChild(sub);
        appendNotebookDownload(doc, inner, definition);
        const chapters = definition.chapters ?? [];
        if (chapters.length > 0) {
            const row = el(doc, 'div', {
                display: 'flex',
                gap: '10px',
                marginTop: '20px',
                flexWrap: 'wrap',
                justifyContent: 'center',
            });
            for (const chapter of chapters) {
                const pill = el(doc, 'span', {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: TOKEN.paperInk,
                    backgroundColor: TOKEN.paperCard,
                    border: `1px solid ${TOKEN.paperLine}`,
                    borderRadius: '999px',
                    padding: '7px 15px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 1px 2px rgba(16,26,44,.05)',
                });
                pill.appendChild(icon(doc, 'check', 12, { color: TOKEN.okInk }));
                pill.appendChild(doc.createTextNode(chapter.name));
                row.appendChild(pill);
            }
            rise(row, 250);
            inner.appendChild(row);
        }
    }

    if (elapsedMs !== undefined) {
        const stat = el(
            doc,
            'div',
            {
                fontFamily: TOKEN.mono,
                fontSize: '11.5px',
                color: TOKEN.paperMute,
                marginTop: '18px',
            },
            `Finished in ${elapsedLabel(elapsedMs)}`,
        );
        rise(stat, 400);
        inner.appendChild(stat);
    }

    const cta = el(doc, 'div', {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '18px',
        flexWrap: 'wrap',
    });
    const primary = button(doc, primaryLabel, 'brand', { big: true });
    primary.addEventListener('click', () => onPrimary());
    const secondary = button(doc, secondaryLabel, 'paper', { big: true });
    secondary.addEventListener('click', () => onSecondary());
    cta.append(primary, secondary);
    rise(cta, 450);
    inner.appendChild(cta);

    if (next) {
        const line = el(doc, 'p', {
            fontSize: '13px',
            color: TOKEN.paperBody,
            margin: '14px 0 0',
        });
        line.appendChild(doc.createTextNode('Up next: '));
        line.appendChild(el(doc, 'b', { color: TOKEN.paperInk, fontWeight: '700' }, next.title));
        const bits = [
            next.minutes ? `${next.minutes} min` : '',
            next.steps ? `${next.steps} steps` : '',
        ].filter(Boolean);
        if (bits.length > 0) line.appendChild(doc.createTextNode(` · ${bits.join(' · ')}`));
        rise(line, 500);
        inner.appendChild(line);
    }

    col.appendChild(inner);
    root.appendChild(col);
    doc.body.appendChild(root);
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onPrimary();
        }
    };
    doc.addEventListener('keydown', onKey);
    primary.focus({ preventScroll: true });

    return () => {
        doc.removeEventListener('keydown', onKey);
        root.remove();
    };
}
